import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { AlertTriangle, Loader2, Brain, CheckCircle, ChevronLeft, Building2, ShieldAlert, Lock } from 'lucide-react';
import { useAppContext } from '../../../core/App';
import BudgetAiAnalysisDisplay from './BudgetAiAnalysisDisplay';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../core/firebase';

const generateBudgetFunction = httpsCallable(functions, 'generateBudget');

const LOADING_MESSAGES = [
  "Analyzing 'Survival Economics'...",
  "Applying 2026 Tax Rules...",
  "Checking Cost of Living...",
  "Scanning for 'Trust Score' Impact...",
  "Drafting your battle plan..."
];

const BudgetStep5 = ({ 
    authUser,
    takeHome, needs, wants, savings, onPrev, onComplete, isReviewMode,
    budgetMode, setBudgetMode, isSurvivalZone, location, avgRent
}) => {
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzingBudget, setIsAnalyzingBudget] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const { goToDashboard } = useAppContext();

  const sumValues = (obj) => Object.values(obj).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);

  const totalNeeds = sumValues(needs);
  const totalWants = sumValues(wants);
  const totalSavings = sumValues(savings);
  const totalBudgeted = totalNeeds + totalWants + totalSavings;
  const leftToBudget = takeHome - totalBudgeted;

  // --- THE FRAMEWORK SPLIT ---
  const isHCOL = budgetMode === 'hcol';
  const NEEDS_TARGET = isHCOL ? 70 : 50;

  // --- SEAL THE ESCAPE HATCH ---
  const isHardBlocked = isSurvivalZone && !isHCOL && leftToBudget < 0;
  
  // Chart Data
  const data = [
    { name: 'Fixed Baselines', value: totalNeeds, color: totalNeeds > (takeHome * (NEEDS_TARGET/100)) ? '#EF4444' : '#10B981' }, 
    { name: 'Flexible Liabilities', value: totalWants, color: '#3B82F6' },
    { name: 'Capital Reserves', value: totalSavings, color: '#8B5CF6' },
  ].filter(item => item.value > 0);

  // --- SMART FEEDBACK LOGIC ---
  const getSmartFeedback = () => {
    if (takeHome === 0) return { text: "Please enter your salary in Step 1.", color: "text-gray-500", icon: AlertTriangle };
    
    const loanPayment = parseFloat(needs.studentLoan || 0);
    if (loanPayment > 0 && loanPayment < 50) {
        return { text: "⚠️ Tax Bomb Warning: Low IDR payments introduce future tax liabilities.", color: "text-orange-600", icon: ShieldAlert };
    }

    if (isSurvivalZone && !isHCOL) {
        return { text: "AUDIT FAILURE: Fixed logistical baselines exceed 35% of income. Standard distribution models are mathematically impossible here. Unlock Survival Mode to recalibrate.", color: "text-red-600", icon: ShieldAlert };
    }

    const needsPercent = totalNeeds / takeHome;
    const targetPercent = NEEDS_TARGET / 100;

    if (needsPercent > targetPercent) {
        return { text: `CRITICAL: Fixed costs exceed survival limits. High risk for operational burnout.`, color: "text-red-600", icon: AlertTriangle };
    }

    if (leftToBudget < -1) {
        return { text: `Deficit detected: $${Math.abs(leftToBudget).toFixed(2)}. Reduce flexible liabilities to stabilize.`, color: "text-red-600", icon: AlertTriangle };
    }
    
    if (isHCOL) {
         return { text: "Strategic Survival: Baseline stabilized in a high-cost market utilizing the 70/20/10 framework.", color: "text-green-600", icon: CheckCircle };
    }

    return { text: "Optimal configuration. Wealth generation scaled via 50/30/20 allocation.", color: "text-blue-600", icon: CheckCircle };
  };

  const feedback = getSmartFeedback();
  const FeedbackIcon = feedback.icon;

  useEffect(() => {
    let interval;
    if (isAnalyzingBudget) {
      let i = 0;
      setLoadingMessage(LOADING_MESSAGES[0]); 
      interval = setInterval(() => {
        i = (i + 1) % LOADING_MESSAGES.length;
        setLoadingMessage(LOADING_MESSAGES[i]);
      }, 1500); 
    }
    return () => clearInterval(interval);
  }, [isAnalyzingBudget]);

  const handleGetAiAnalysis = async () => {
    setIsAnalyzingBudget(true);
    setAnalysisError(null);
    try {
       const payload = {
           monthly_income: takeHome,
           mode: budgetMode,
           isSurvivalZone: isSurvivalZone,
           location: location,
           avgRent: avgRent,
           user_allocation: { needs: totalNeeds, wants: totalWants, savings: totalSavings },
           expense_breakdown: { ...needs, ...wants, ...savings }
       };

       const result = await generateBudgetFunction({ payload });
       
       if (!result || !result.data || !result.data.coaching_briefing) {
           throw new Error("AI response incomplete.");
       }
       
       setAiAnalysis(result.data);
    } catch (e) {
      console.error("Budget AI Error:", e);
      setAnalysisError("Coach is offline. Please try again.");
    } finally {
      setIsAnalyzingBudget(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6 relative">
      
      {/* MODE TOGGLE */}
      <div className="flex justify-center mb-4">
        <div className="bg-gray-100 p-1 rounded-lg flex gap-1">
            <button 
                onClick={() => setBudgetMode('standard')}
                className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${budgetMode === 'standard' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
                Standard (50/30/20)
            </button>
            <button 
                // CRITICAL PATCH: Removed B2C upgrade trigger. Mode selection is now fully unlocked.
                onClick={() => setBudgetMode('hcol')}
                className={`px-4 py-2 text-xs font-bold rounded-md transition-all flex items-center gap-1 ${budgetMode === 'hcol' ? 'bg-white shadow text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
                <Building2 size={12} /> Survival (70/20/10)
            </button>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-800 text-center">Your Money Blueprint</h2>

      <div className={`p-4 rounded-lg border flex items-start ${feedback.color.replace('text', 'bg').replace('600', '50')} ${feedback.color.replace('text', 'border').replace('600', '200')}`}>
        <FeedbackIcon className={`w-6 h-6 mr-3 flex-shrink-0 ${feedback.color}`} />
        <p className={`font-medium ${feedback.color}`}>{feedback.text}</p>
      </div>

      <div className="h-64 w-full relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Legend verticalAlign="bottom" height={36} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Target</span>
                <div className={`text-xl font-black ${isHCOL ? 'text-orange-500' : 'text-blue-500'}`}>
                    {isHCOL ? "70/20/10" : "50/30/20"}
                </div>
            </div>
        </div>
      </div>

      {/* BLURRED GATING MASK */}
      <div className="relative mt-8">
         {!authUser && (
             <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-md rounded-2xl">
                 <div className="bg-white p-6 rounded-2xl shadow-xl max-w-sm text-center border border-slate-200">
                    <Lock className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                    <h3 className="text-xl font-black text-slate-900 mb-2">Coach's Analysis Locked</h3>
                    <p className="text-slate-600 mb-4 text-sm font-medium">To unlock your verified data, correct your skill gaps, and access the Survival Gauntlet, connect via Google Sign-On.</p>
                    <button onClick={() => goToDashboard()} className="w-full bg-blue-600 text-white font-black py-4 rounded-xl shadow-lg hover:bg-blue-700 transition">Unlock Full Blueprint</button>
                 </div>
             </div>
         )}
         
         <div className={!authUser ? "opacity-30 blur-md pointer-events-none select-none pt-6 border-t" : "pt-6 border-t"}>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Ask the Coach</h3>
            <p className="text-gray-600 mb-4 text-sm">
                Get a breakdown based on the <strong>{isHCOL ? "Survival Economics" : "Standard"}</strong> framework.
            </p>
            
            {!aiAnalysis && (
                <button
                onClick={handleGetAiAnalysis}
                disabled={isAnalyzingBudget || takeHome === 0}
                className="w-full flex items-center justify-center px-6 py-3 bg-indigo-600 text-white font-bold rounded-lg shadow-md hover:bg-indigo-700 transition disabled:opacity-50"
                >
                {isAnalyzingBudget ? (
                    <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        {loadingMessage}
                    </>
                ) : (
                    <>
                        <Brain className="w-5 h-5 mr-2" />
                        Analyze My Plan
                    </>
                )}
                </button>
            )}
            
            {analysisError && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg">{analysisError}</div>}
            {aiAnalysis && <BudgetAiAnalysisDisplay report={aiAnalysis} />}
         </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 border-t">
        <button onClick={onPrev} className="text-gray-700 font-medium py-3 px-6 rounded-lg hover:bg-gray-100 flex items-center">
          <ChevronLeft className="w-5 h-5 mr-1" />
          {isReviewMode ? "Review Inputs" : "Back to Savings"}
        </button>
        {!isReviewMode && authUser && (
             <button 
                onClick={onComplete} 
                disabled={isHardBlocked} 
                className={`bg-green-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg transition 
                    ${isHardBlocked ? 'opacity-50 cursor-not-allowed grayscale' : 'hover:bg-green-700 hover:-translate-y-1'}`}
             >
                {isHardBlocked ? "Math Error: Incomplete" : "Complete Simulation"}
             </button>
        )}
      </div>
    </div>
  );
};

export default BudgetStep5;